import { useEffect } from "react";
import { logToSentry } from "../utils/logger";
import { evaluateLivenessTick, getLastInboundAt, resetSubscriptionLiveness } from "../utils/subscriptionLiveness";

type WatchdogParams = {
	appConnected: boolean;
	shouldSkip: boolean;
	isSubscriptionBroken: boolean;
	fridgeId: string;
	staleThresholdMs: number;
	setIsSubscriptionBroken: (broken: boolean) => void;
};

/**
 * Passive liveness watchdog — a BACKSTOP to the AppSync link's own keepalive timeout, not the primary
 * detector. `aws-appsync-subscription-link` already errors the subscription (→ onError → broken) when no
 * `ka` arrives within its keepAliveTimeoutMs (wired to the same connectionTimeout), and it fires slightly
 * sooner. What that library timer does NOT cover is the bootstrapping hole: it is armed only on the FIRST
 * `ka`, so a socket that completes the handshake (connection_ack + start_ack) and then goes silent BEFORE
 * any `ka` ever arrives — plausible on a flaky mobile link, and passed through on every reconnect — is
 * caught by nothing else. This watchdog fills exactly that gap: it declares the stream broken if NOTHING
 * (ka keepalive or data frame) has arrived within staleThresholdMs while we are online and not skipped. `ka`
 * flows even when the fridge is idle, so a healthy quiet stream never trips; only a dead socket does.
 * Paused/reset when offline, when intentionally skipped, or when already known broken.
 *
 * NOTE: it deliberately CANNOT catch a `ka`-alive / data-dead zombie (a `ka` keeps it green) — that
 * half-open "connected but not delivering" case is detected out-of-band by the QR-drift check on the hourly
 * refetch (utils/qrDrift), which is the only signal that a transaction was silently missed.
 */
export const useSubscriptionLivenessWatchdog = ({
	appConnected,
	shouldSkip,
	isSubscriptionBroken,
	fridgeId,
	staleThresholdMs,
	setIsSubscriptionBroken,
}: WatchdogParams): void => {
	useEffect(() => {
		if (!appConnected || shouldSkip || isSubscriptionBroken) {
			return;
		}
		// Starting to watch (online, not skipped, not broken): re-baseline so the gap before the first
		// ka/frame after a (re)connect doesn't count as staleness.
		resetSubscriptionLiveness();
		const tickMs = Math.max(1000, Math.floor(staleThresholdMs / 4));
		let lastTickAt = Date.now();
		const intervalId = window.setInterval(() => {
			const now = Date.now();
			const action = evaluateLivenessTick(now, lastTickAt, getLastInboundAt(), staleThresholdMs);
			lastTickAt = now;
			if (action === "rebaseline") {
				// Clock jumped (kiosk woke from suspend) — not a quiet stream. Restart the window.
				resetSubscriptionLiveness();
				return;
			}
			if (action === "trip") {
				logToSentry(
					`Subscription liveness watchdog tripped (no ka/frame within ${staleThresholdMs}ms), fridge: ${fridgeId}`,
					"warning",
				);
				setIsSubscriptionBroken(true);
			}
		}, tickMs);
		return () => window.clearInterval(intervalId);
	}, [appConnected, shouldSkip, isSubscriptionBroken, fridgeId, staleThresholdMs, setIsSubscriptionBroken]);
};
